import React, { useState } from 'react';
import { initials as lettersOf } from '../lib/initials';
import { forgetPicture, usePhotoSrc } from '../lib/photos';
import './Avatar.css';

/**
 * A PERSON'S CIRCLE - their photo when they have one, their initials when not.
 *
 * Gordon: "Allow users to upload a picture of themselves so that the round
 * circles in the chat can be exchanged into a face pic."
 *
 * One component for every circle in Jarvis, so a photo shows everywhere a
 * person does and nowhere differs about whether they have one. It keeps
 * whatever circle the place already drew: the call site passes its own class
 * (ch-avatar, tds-av, se-tile-face, am-avatar) and its own tint, and this
 * draws exactly that - same size, same shape, same letters - with the photo
 * laid over it once it has arrived. So nothing moves when a photo loads, and
 * a photo that is missing, slow or broken is simply the circle that was
 * always there. Anything else the place puts inside (a presence dot) is drawn
 * after the photo, so it stays on top.
 *
 *   <Avatar person={{ key: 'u:…', name: 'Rita Gatt' }} className="ch-avatar"
 *           data-hue={hueOf(key)} aria-hidden="true">
 *     <PresenceDot … />
 *   </Avatar>
 *
 * `size` is roughly how large it is drawn, in CSS pixels: anything under 72
 * uses the 96-pixel picture (sharp up to ~48px on a retina screen), larger
 * uses the 512. `text` replaces the letters for a place with its own rule
 * for them. Only a key ('u:<id>' / 'c:<id>') can have a photo - a guest,
 * named by email or by nothing, always gets letters.
 */
export default function Avatar({
  person, className = '', size = 32, text = null, children = null, ...rest
}) {
  const key = person && typeof person.key === 'string' && /^[uc]:/.test(person.key) ? person.key : null;
  const src = usePhotoSrc(key, size >= 72 ? 512 : 96);
  const [loadedSrc, setLoadedSrc] = useState(null);
  const loaded = Boolean(src) && loadedSrc === src;
  return (
    <span className={`${className} jv-av`.trim()} data-photo={loaded ? 'on' : undefined} {...rest}>
      <span className="jv-av-text">{text ?? lettersOf(person?.name)}</span>
      {src && (
        <img
          className="jv-av-img"
          src={src}
          alt=""
          draggable={false}
          decoding="async"
          data-loaded={loaded || undefined}
          onLoad={() => setLoadedSrc(src)}
          onError={() => { forgetPicture(src); setLoadedSrc(null); }}
        />
      )}
      {children}
    </span>
  );
}
